export type LocalAuthField = 'login' | 'password' | 'email' | 'referralCode' | 'loginOrEmail';

export type LocalAuthErrorCode =
  | 'LOGIN_INVALID'
  | 'LOGIN_REQUIRED'
  | 'LOGIN_TAKEN'
  | 'LOGIN_NOT_FOUND'
  | 'PASSWORD_INVALID'
  | 'PASSWORD_REQUIRED'
  | 'WRONG_PASSWORD'
  | 'EMAIL_INVALID'
  | 'EMAIL_TAKEN'
  | 'EMAIL_PENDING_VERIFICATION'
  | 'EMAIL_NOT_VERIFIED'
  | 'EMAIL_SEND_FAILED'
  | 'EMAIL_NOT_CONFIGURED'
  | 'REFERRAL_CODE_INVALID'
  | 'AUTH_USE_OAUTH'
  | 'USER_BANNED'
  | 'RESEND_NOT_FOUND'
  | 'ACCOUNT_ALREADY_VERIFIED'
  | 'PASSWORD_RESET_NOT_FOUND'
  | 'PASSWORD_RESET_INVALID'
  | 'CAPTCHA_REQUIRED'
  | 'CAPTCHA_INVALID';

export interface LocalAuthErrorPayload {
  message: string;
  status: number;
  errorCode?: LocalAuthErrorCode | string;
  field?: LocalAuthField;
  emailVerificationRequired?: boolean;
  retryAfter?: number;
}

/** Ошибки, которые показываем только у поля (без глобального баннера). */
export const FIELD_ONLY_ERROR_CODES: readonly string[] = [
  'LOGIN_INVALID',
  'LOGIN_REQUIRED',
  'PASSWORD_REQUIRED',
  'PASSWORD_INVALID',
  'EMAIL_INVALID',
  'EMAIL_SEND_FAILED',
  'WRONG_PASSWORD',
];

export function fieldErrorsFromPayload(
  payload: LocalAuthErrorPayload
): Partial<Record<LocalAuthField, string>> {
  if (payload.field && payload.message && payload.field !== 'referralCode') {
    return { [payload.field]: payload.message };
  }
  return {};
}

export function isFieldOnlyError(payload: LocalAuthErrorPayload): boolean {
  if (!payload.field || payload.field === 'referralCode') return false;
  return FIELD_ONLY_ERROR_CODES.includes(payload.errorCode ?? '');
}

export function shouldShowGlobalError(payload: LocalAuthErrorPayload): boolean {
  if (payload.field === 'referralCode') return true;
  return !isFieldOnlyError(payload);
}

export function shouldGoToCheckEmail(errorCode?: string, message?: string): boolean {
  if (errorCode === 'EMAIL_NOT_VERIFIED' || errorCode === 'EMAIL_PENDING_VERIFICATION') {
    return true;
  }
  if (errorCode === 'LOGIN_TAKEN' && message?.toLowerCase().includes('не подтвержд')) {
    return true;
  }
  return Boolean(errorCode === undefined && message?.toLowerCase().includes('подтвердите email'));
}

export function isOAuthRequired(errorCode?: string): boolean {
  return errorCode === 'AUTH_USE_OAUTH';
}

export function isLoginNotFound(errorCode?: string): boolean {
  return errorCode === 'LOGIN_NOT_FOUND';
}

export function isPasswordResetNotFound(errorCode?: string): boolean {
  return errorCode === 'PASSWORD_RESET_NOT_FOUND';
}

export function isPasswordResetInvalid(errorCode?: string): boolean {
  return errorCode === 'PASSWORD_RESET_INVALID';
}

export function isAccountAlreadyVerified(errorCode?: string): boolean {
  return errorCode === 'ACCOUNT_ALREADY_VERIFIED';
}

export function isUserBanned(errorCode?: string): boolean {
  return errorCode === 'USER_BANNED';
}

export function isCaptchaError(errorCode?: string): boolean {
  return errorCode === 'CAPTCHA_REQUIRED' || errorCode === 'CAPTCHA_INVALID';
}

/** Понятные сообщения по errorCode, если сервер не вернул message. */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  LOGIN_INVALID: 'Логин: 6–20 символов, только латиница и цифры',
  LOGIN_REQUIRED: 'Укажите логин или email',
  LOGIN_TAKEN: 'Этот логин уже занят. Выберите другой или войдите в аккаунт',
  LOGIN_NOT_FOUND: 'Пользователь с таким логином не найден. Проверьте логин или зарегистрируйтесь',
  PASSWORD_INVALID: 'Пароль не соответствует требованиям: от 8 символов, заглавная и строчная буквы, цифра и спецсимвол',
  PASSWORD_REQUIRED: 'Укажите пароль',
  WRONG_PASSWORD: 'Неверный пароль. Проверьте ввод или восстановите пароль через «Забыли пароль?»',
  EMAIL_INVALID: 'Введите корректный email',
  EMAIL_TAKEN: 'Этот email уже используется. Войдите или восстановите пароль',
  EMAIL_PENDING_VERIFICATION: 'Письмо уже отправлено — проверьте почту или запросите повторную отправку',
  EMAIL_NOT_VERIFIED: 'Подтвердите email перед входом. Проверьте почту или запросите письмо повторно',
  EMAIL_SEND_FAILED: 'Не удалось отправить письмо. Попробуйте позже или обратитесь в поддержку',
  EMAIL_NOT_CONFIGURED: 'Отправка писем временно недоступна. Попробуйте позже',
  REFERRAL_CODE_INVALID: 'Реферальный код не найден или недействителен',
  AUTH_USE_OAUTH: 'Этот аккаунт создан через Telegram или Яндекс. Войдите этим способом',
  USER_BANNED: 'Аккаунт заблокирован. Обратитесь в поддержку',
  RESEND_NOT_FOUND: 'Аккаунт с таким логином или email не найден. Проверьте данные или зарегистрируйтесь',
  ACCOUNT_ALREADY_VERIFIED: 'Email уже подтверждён — можно войти в аккаунт',
  PASSWORD_RESET_NOT_FOUND: 'Аккаунт не найден. Проверьте логин или email, либо зарегистрируйтесь',
  PASSWORD_RESET_INVALID: 'Ссылка для сброса пароля недействительна или истекла. Запросите новую',
  EMAIL_VERIFICATION_INVALID: 'Ссылка подтверждения недействительна или истекла. Запросите письмо повторно',
  CAPTCHA_REQUIRED: 'Подтвердите, что вы не робот',
  CAPTCHA_INVALID: 'Проверка капчи не пройдена. Пройдите её снова',
};

const GENERIC_SERVER_MESSAGES = new Set([
  'Произошла ошибка. Попробуйте позже.',
  'Произошла ошибка. Попробуйте позже',
  'Internal Server Error',
  'login-error',
  'verify-email-error',
  'reset-password-error',
]);

function messageForHttpStatus(status: number, retryAfter?: number): string {
  if (status === 0) {
    return 'Не удалось связаться с сервером. Проверьте подключение к интернету';
  }
  if (status === 429) {
    return retryAfter
      ? `Слишком много попыток. Повторите через ${retryAfter} с`
      : 'Слишком много попыток. Подождите и попробуйте снова';
  }
  if (status === 401) {
    return 'Неверные данные для входа. Проверьте логин и пароль';
  }
  if (status === 403) {
    return 'Доступ запрещён. Проверьте статус аккаунта или подтвердите email';
  }
  if (status === 404) {
    return 'Аккаунт не найден. Проверьте логин или email';
  }
  if (status === 409) {
    return 'Данные уже используются другим аккаунтом';
  }
  if (status >= 500) {
    return 'Сервер временно недоступен. Попробуйте через несколько минут';
  }
  return 'Не удалось выполнить запрос. Проверьте данные и попробуйте снова';
}

export function resolveLocalAuthMessage(params: {
  message?: string;
  errorCode?: string;
  status: number;
  retryAfter?: number;
}): string {
  const trimmed = params.message?.trim();
  if (trimmed && !GENERIC_SERVER_MESSAGES.has(trimmed)) {
    return trimmed;
  }

  if (params.errorCode && ERROR_CODE_MESSAGES[params.errorCode]) {
    return ERROR_CODE_MESSAGES[params.errorCode];
  }

  return messageForHttpStatus(params.status, params.retryAfter);
}

export function toLocalAuthErrorPayload(err: unknown): LocalAuthErrorPayload {
  if (err instanceof Error && err.name === 'LocalAuthApiError') {
    const apiErr = err as unknown as LocalAuthErrorPayload & {
      toPayload?: () => LocalAuthErrorPayload;
    };
    const raw = apiErr.toPayload?.() ?? apiErr;
    return {
      ...raw,
      message: resolveLocalAuthMessage({
        message: raw.message,
        errorCode: raw.errorCode,
        status: raw.status,
        retryAfter: raw.retryAfter,
      }),
    };
  }

  if (typeof err === 'object' && err && 'message' in err) {
    const payload = err as LocalAuthErrorPayload;
    return {
      status: payload.status ?? 0,
      errorCode: payload.errorCode,
      field: payload.field,
      emailVerificationRequired: payload.emailVerificationRequired,
      retryAfter: payload.retryAfter,
      message: resolveLocalAuthMessage({
        message: typeof payload.message === 'string' ? payload.message : undefined,
        errorCode: payload.errorCode,
        status: payload.status ?? 0,
        retryAfter: payload.retryAfter,
      }),
    };
  }

  if (typeof err === 'string' && err.trim() && !GENERIC_SERVER_MESSAGES.has(err)) {
    return { message: err, status: 0 };
  }

  return {
    message: messageForHttpStatus(0),
    status: 0,
  };
}
